import type { TRPCRouter } from "@alch/dx-webapp-api";
import { SearchParam } from "@hooks/useSearchParam";
import type { QueryClient } from "@tanstack/react-query";
import type { TRPCClient } from "@trpc/client";
import type { createTRPCReact, TRPCLink } from "@trpc/react-query";
import { TRPCClientError } from "@trpc/react-query";
import type { AnyTRPCRouter } from "@trpc/server";
import { observable } from "@trpc/server/observable";
import { getViewAsId } from "@util/viewAs";
import type { createContext } from "react";
import { getStoredAuthToken, logOutFromAuthchemy } from "../auth.js";

export interface TrpcProviderProps<TRouter extends AnyTRPCRouter> {
  trpc: ReturnType<typeof createTRPCReact<TRouter>>;
  trpcClient: TRPCClient<TRouter>;
  reactQueryContext?: ReturnType<typeof createContext<QueryClient | undefined>>;
}

// Middleware to block mutations when viewing as another team or user
export const blockImpersonationMutations: TRPCLink<TRPCRouter> = () => {
  return ({ op, next }) => {
    const viewAsId = getViewAsId();

    if (viewAsId && op.type !== "query") {
      return observable((observer) => {
        observer.error(
          new TRPCClientError(
            "Cannot perform mutations while viewing as someone else.",
          ),
        );
      });
    }

    return next(op);
  };
};

export function createHttpLinksOpt(apiOrigin: string) {
  let isLogoutInProgress = false;

  return {
    url: `${apiOrigin}/trpc`,
    headers() {
      const headers: Record<string, string> = {};
      const authToken = getStoredAuthToken();
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      return headers;
    },
    fetch: (
      url: string | URL | RequestInfo,
      options: RequestInit | undefined,
    ) => {
      let modifiedUrl = url;

      // This is always true
      if (typeof url === "string") {
        const { view_as_user, view_as_team } = getViewAsId() ?? {};

        const urlObj = new URL(url);
        if (view_as_user) {
          urlObj.searchParams.set(SearchParam.ViewAsUser, view_as_user);
        }
        if (view_as_team) {
          urlObj.searchParams.set(SearchParam.ViewAsTeam, view_as_team);
        }
        modifiedUrl = urlObj.toString();
      }

      return fetch(modifiedUrl, {
        ...options,
        // This is required for deviceId cookie used for flags
        credentials: "include",
        // Send the page URL the fetch call was made from so backend sentry can see it
        referrerPolicy: "no-referrer-when-downgrade",
      }).then((response) => {
        if (response.status === 401) {
          if (!isLogoutInProgress) {
            isLogoutInProgress = true;
            logOutFromAuthchemy();
          }
          throw new TRPCClientError("Unauthorized");
        }
        if (response.status === 429) {
          throw new TRPCClientError("Rate limit exceeded");
        }
        return response;
      });
    },
  };
}
